import { useState } from 'react';
import { addDays, can, EVENT_META, localDate, type EventType } from '@bb/shared/core';
import { api, ApiError } from '../api';
import { Chips, Seg, Sheet } from '../components';
import { deleteWithUndo } from '../actions';
import { isoToZoned, zonedToIso } from '../lib';
import { platform } from '../platform';
import { useApp, type EditorTarget } from '../state';

type P = Record<string, unknown>;

function defaults(type: EventType): P {
  switch (type) {
    case 'breast':
      return { side: 'left' };
    case 'bottle':
      return { ml: 120, kind: 'formula' };
    case 'diaper':
      return { kind: 'wet' };
    case 'temperature':
      return { celsius: 36.6 };
    default:
      return {};
  }
}

const str = (v: unknown) => (v == null ? '' : String(v));
const numOrUndef = (s: string) => {
  const n = Number(s.replace(',', '.'));
  return s.trim() === '' || !Number.isFinite(n) ? undefined : n;
};

/** Поля содержимого записи для каждого типа. */
function PayloadFields({ type, p, set }: { type: EventType; p: P; set: (patch: P) => void }) {
  switch (type) {
    case 'breast':
      return (
        <div className="field">
          <span className="label">Грудь</span>
          <Seg
            label="Грудь"
            value={str(p.side) || 'left'}
            options={[
              { value: 'left', label: 'Левая' },
              { value: 'right', label: 'Правая' },
              { value: 'both', label: 'Обе' },
            ]}
            onChange={(side) => set({ side })}
          />
        </div>
      );
    case 'bottle': {
      const ml = Number(p.ml) || 0;
      return (
        <>
          <div className="field">
            <span className="label">Объём, мл</span>
            <div className="stepper">
              <button type="button" aria-label="Меньше на 10 мл" onClick={() => set({ ml: Math.max(10, ml - 10) })}>
                −
              </button>
              <span className="num" data-testid="ml">
                {ml}
              </span>
              <button type="button" className="plus" aria-label="Больше на 10 мл" onClick={() => set({ ml: Math.min(500, ml + 10) })}>
                +
              </button>
            </div>
          </div>
          <Seg
            label="Что в бутылочке"
            value={str(p.kind) || 'formula'}
            options={[
              { value: 'formula', label: 'Смесь' },
              { value: 'breast_milk', label: 'Сцеженное' },
            ]}
            onChange={(kind) => set({ kind })}
          />
        </>
      );
    }
    case 'solid':
      return (
        <>
          <div className="field">
            <label htmlFor="f-food">Что ел</label>
            <input id="f-food" className="input" value={str(p.food)} maxLength={80} onChange={(e) => set({ food: e.target.value })} placeholder="Кабачок" />
          </div>
          <div className="field">
            <label htmlFor="f-amount">Сколько (необязательно)</label>
            <input id="f-amount" className="input" value={str(p.amount)} maxLength={40} onChange={(e) => set({ amount: e.target.value || undefined })} placeholder="3 ложки" />
          </div>
        </>
      );
    case 'diaper':
      return (
        <div className="field">
          <span className="label">Какой</span>
          <Chips
            label="Подгузник"
            value={str(p.kind) || null}
            options={[
              { value: 'wet', label: 'Мокрый' },
              { value: 'dirty', label: 'Грязный' },
              { value: 'mixed', label: 'Оба' },
              { value: 'dry', label: 'Сухой' },
            ]}
            onChange={(kind) => set({ kind })}
          />
        </div>
      );
    case 'medicine':
      return (
        <>
          <div className="field">
            <label htmlFor="f-med">Лекарство</label>
            <input id="f-med" className="input" value={str(p.name)} maxLength={80} onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="f-dose">Доза (необязательно)</label>
            <input id="f-dose" className="input" value={str(p.dose)} maxLength={40} onChange={(e) => set({ dose: e.target.value || undefined })} />
          </div>
        </>
      );
    case 'temperature':
      return (
        <div className="field">
          <label htmlFor="f-temp">Температура, °C</label>
          <input id="f-temp" className="input" inputMode="decimal" value={str(p.celsius)} onChange={(e) => set({ celsius: e.target.value })} />
        </div>
      );
    case 'measure':
      return (
        <div className="tiles3">
          <div className="field">
            <label htmlFor="f-w">Вес, кг</label>
            <input id="f-w" className="input" inputMode="decimal" value={str(p.weightKg)} onChange={(e) => set({ weightKg: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="f-h">Рост, см</label>
            <input id="f-h" className="input" inputMode="decimal" value={str(p.heightCm)} onChange={(e) => set({ heightCm: e.target.value })} />
          </div>
          <div className="field">
            <label htmlFor="f-hd">Голова, см</label>
            <input id="f-hd" className="input" inputMode="decimal" value={str(p.headCm)} onChange={(e) => set({ headCm: e.target.value })} />
          </div>
        </div>
      );
    case 'note':
      return (
        <div className="field">
          <label htmlFor="f-note">Заметка</label>
          <textarea id="f-note" className="input" value={str(p.text)} maxLength={2000} onChange={(e) => set({ text: e.target.value })} />
        </div>
      );
    default:
      return null;
  }
}

/** Собирает payload для API; возвращает текст ошибки, если поле не заполнено. */
function buildPayload(type: EventType, p: P): P | string {
  switch (type) {
    case 'breast': {
      // Если сторону поменяли вручную — отрезки больше не соответствуют, убираем их.
      const keepSegs = Array.isArray(p.segments) && p.side === p._origSide;
      return { side: p.side ?? 'left', ...(keepSegs ? { segments: p.segments } : {}) };
    }
    case 'sleep': {
      const { place, position, clothes, roomTemp, settleStartedAt, methods } = p as Record<string, unknown>;
      return {
        ...(typeof settleStartedAt === 'string' ? { settleStartedAt } : {}),
        ...(Array.isArray(methods) && methods.length ? { methods } : {}),
        ...(typeof place === 'string' && place ? { place } : {}),
        ...(typeof position === 'string' && position ? { position } : {}),
        ...(Array.isArray(clothes) && clothes.length ? { clothes } : {}),
        ...(typeof roomTemp === 'number' ? { roomTemp } : {}),
      };
    }
    case 'bottle':
      return {
        ml: Number(p.ml),
        kind: p.kind ?? 'formula',
        ...(typeof p.preparedMl === 'number' ? { preparedMl: Math.max(Number(p.ml), p.preparedMl) } : {}),
        ...(typeof p.preparedMl === 'number' ? { leftMl: Math.max(0, Math.max(Number(p.ml), p.preparedMl) - Number(p.ml)) } : {}),
      };
    case 'solid':
      if (!str(p.food).trim()) return 'Напишите, что ел малыш';
      return { food: str(p.food).trim(), ...(str(p.amount).trim() ? { amount: str(p.amount).trim() } : {}) };
    case 'diaper':
      return { kind: p.kind ?? 'wet' };
    case 'medicine':
      if (!str(p.name).trim()) return 'Укажите лекарство';
      return { name: str(p.name).trim(), ...(str(p.dose).trim() ? { dose: str(p.dose).trim() } : {}) };
    case 'temperature': {
      const c = numOrUndef(str(p.celsius));
      if (c === undefined || c < 34 || c > 43) return 'Температура должна быть от 34 до 43 °C';
      return { celsius: c };
    }
    case 'measure': {
      const w = numOrUndef(str(p.weightKg));
      const h = numOrUndef(str(p.heightCm));
      const hd = numOrUndef(str(p.headCm));
      if (w === undefined && h === undefined && hd === undefined) return 'Заполните хотя бы одно поле';
      return {
        ...(w !== undefined ? { weightG: Math.round(w * 1000) } : {}),
        ...(h !== undefined ? { heightCm: h } : {}),
        ...(hd !== undefined ? { headCm: hd } : {}),
      };
    }
    case 'note':
      if (!str(p.text).trim()) return 'Заметка пустая';
      return { text: str(p.text).trim() };
    default:
      return {};
  }
}

/** Из сохранённой записи — в значения полей формы. */
function toForm(type: EventType, payload: P): P {
  if (type === 'measure') {
    return {
      weightKg: typeof payload.weightG === 'number' ? String(payload.weightG / 1000) : '',
      heightCm: str(payload.heightCm),
      headCm: str(payload.headCm),
    };
  }
  if (type === 'temperature') return { celsius: str(payload.celsius) };
  if (type === 'breast') return { ...payload, _origSide: payload.side };
  return { ...payload };
}

export function EventEditor({ target, onClose }: { target: EditorTarget; onClose: () => void }) {
  const ctx = useApp();
  const { family } = ctx;
  const tz = family.tz;
  const ev = target.event;
  const type = ev?.type ?? target.type;
  const meta = EVENT_META[type];
  const isEdit = !!ev;

  const nowZ = isoToZoned(Date.now(), tz);
  const startZ = ev ? isoToZoned(ev.startedAt, tz) : nowZ;
  const endZ = ev?.endedAt ? isoToZoned(ev.endedAt, tz) : null;

  const [mode, setMode] = useState<'now' | 'past'>(isEdit ? 'past' : 'now');
  const [date, setDate] = useState(startZ.date);
  const [start, setStart] = useState(startZ.time);
  const [end, setEnd] = useState(endZ?.time ?? '');
  const [p, setP] = useState<P>(() => (ev ? toForm(type, ev.payload) : { ...defaults(type), ...target.preset }));
  const [note, setNote] = useState(ev?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canDelete = isEdit && (ev.authorId === ctx.me.user.id || can(family.role, 'events.deleteOthers'));
  const startsTimer = !isEdit && mode === 'now' && meta.timed;
  const today = localDate(Date.now(), tz);

  async function save() {
    setError(null);
    const payload = buildPayload(type, p);
    if (typeof payload === 'string') return setError(payload);

    let startedAt: string;
    let endedAt: string | undefined;
    if (mode === 'now') {
      startedAt = new Date().toISOString();
    } else {
      if (!date || !start) return setError('Укажите дату и время');
      startedAt = zonedToIso(date, start, tz);
      if (meta.timed) {
        if (!end && !(isEdit && !ev.endedAt) && type !== 'bottle') return setError('Укажите, когда закончилось');
        if (!end && type === 'bottle' && !(isEdit && !ev.endedAt)) endedAt = startedAt;
        if (end) endedAt = zonedToIso(end < start ? addDays(date, 1) : date, end, tz);
      }
      if (Date.parse(startedAt) > Date.now() + 5 * 60000) return setError('Время ещё не наступило');
      if (endedAt && Date.parse(endedAt) > Date.now() + 5 * 60000) return setError('Время окончания ещё не наступило');
    }
    const noteVal = type === 'note' ? undefined : note.trim() || undefined;

    setBusy(true);
    try {
      if (isEdit) {
        await api.updateEvent(ev.id, {
          startedAt,
          ...(endedAt ? { endedAt } : {}),
          payload,
          note: type === 'note' ? undefined : (note.trim() || null),
        });
        ctx.toast({ text: 'Запись изменена' });
      } else {
        await api.createEvent(ctx.child.id, { type, startedAt, ...(endedAt ? { endedAt } : {}), payload, ...(noteVal ? { note: noteVal } : {}) });
        ctx.toast({ text: startsTimer ? 'Таймер запущен' : 'Записано' });
      }
      platform.haptic('success');
      ctx.bump();
      onClose();
    } catch (e) {
      setBusy(false);
      platform.haptic('error');
      setError(e instanceof ApiError ? (e.code === 'timer_running' ? 'Такой таймер уже идёт — сначала остановите его' : e.message) : 'Не получилось сохранить');
    }
  }

  async function remove() {
    if (!ev) return;
    if (!(await platform.confirm('Удалить запись?'))) return;
    onClose();
    await deleteWithUndo(ctx, ev);
  }

  return (
    <Sheet title={isEdit ? `${meta.label} · изменить` : meta.label} onClose={onClose}>
      {!isEdit && (
        <Seg
          label="Когда"
          value={mode}
          options={[
            { value: 'now', label: meta.timed ? 'Сейчас, таймер' : 'Сейчас' },
            { value: 'past', label: 'Раньше' },
          ]}
          onChange={setMode}
        />
      )}

      {mode === 'past' && (
        <div className="stack">
          <div className="field">
            <label htmlFor="f-date">Дата</label>
            <input id="f-date" className="input" type="date" max={today} value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className={meta.timed ? 'tiles2' : ''}>
            <div className="field">
              <label htmlFor="f-start">{meta.timed ? 'Начало' : 'Время'}</label>
              <input id="f-start" className="input" type="time" value={start} onChange={(e) => setStart(e.target.value)} />
            </div>
            {meta.timed && (
              <div className="field">
                <label htmlFor="f-end">Конец</label>
                <input id="f-end" className="input" type="time" value={end} onChange={(e) => setEnd(e.target.value)} placeholder={isEdit && !ev.endedAt ? 'идёт' : ''} />
              </div>
            )}
          </div>
          {isEdit && meta.timed && !ev.endedAt && <p className="tiny muted">Таймер ещё идёт. Укажите конец, чтобы остановить его этим временем.</p>}
        </div>
      )}

      <PayloadFields type={type} p={p} set={(patch) => setP((prev) => ({ ...prev, ...patch }))} />

      {type !== 'note' && (
        <div className="field">
          <label htmlFor="f-comment">Комментарий</label>
          <input id="f-comment" className="input" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} placeholder="Необязательно" />
        </div>
      )}

      {isEdit && (ev.authorName || ev.editedBy) && (
        <p className="tiny muted">
          {ev.authorName ? `Добавил(а): ${ev.authorName}` : ''}
          {ev.editedBy ? ' · запись изменяли' : ''}
        </p>
      )}

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <button type="button" className="btn btn-primary btn-block" disabled={busy} onClick={() => void save()}>
        {busy ? 'Сохраняем…' : startsTimer ? 'Запустить таймер' : 'Сохранить'}
      </button>
      {canDelete && (
        <button type="button" className="btn btn-danger btn-block" disabled={busy} onClick={() => void remove()}>
          Удалить запись
        </button>
      )}
    </Sheet>
  );
}
